/**
 * The composer's context wheel.
 *
 * The wheel fills with how much of the model's context the next turn will
 * use (the server's estimate, src/core/compaction.py) and shows the
 * percentage in its middle. Clicking it asks, in a dialog, whether to fold
 * the older turns into a summary now (no separate button: one next to the
 * mic was too easy to hit by mistake);
 * automatic compaction (the Settings toggle) does the same between turns once
 * the wheel reaches the server's threshold. Where a conversation was
 * compacted, the chat shows a divider that opens to the summary.
 */

import { state } from './state.js';
import { elements, forceScrollToBottom } from './ui.js';
import { fetchSessionContext, compactSession } from './api.js';
import { showToast } from './toast.js';

const RING_LENGTH = 2 * Math.PI * 15; // r=15 in a 36-unit viewBox

let wheel = null;
let seenSession = undefined;
let pollTimer = null;
let busy = false;
let last = null;

function fmtTokens(n) {
    if (n === null || n === undefined) return '?';
    return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
}

function render(ctx) {
    if (!wheel) return;
    last = ctx;
    const ring = wheel.querySelector('.context-wheel-fill');
    const label = wheel.querySelector('.context-wheel-pct');
    const compacting = busy || !!ctx?.compacting;
    wheel.classList.toggle('compacting', compacting);

    if (!ctx) {
        ring.style.strokeDashoffset = RING_LENGTH;
        label.textContent = '0';
        wheel.dataset.level = 'low';
        wheel.title = 'Context: nothing used yet. It fills as the conversation grows.';
        return;
    }
    const pct = Math.max(0, ctx.percent || 0);
    const shown = Math.min(100, pct);
    ring.style.strokeDashoffset = RING_LENGTH * (1 - shown / 100);
    label.textContent = compacting ? '' : String(Math.round(shown));
    const limit = ctx.threshold_percent || 80;
    wheel.dataset.level = pct >= limit ? 'high' : pct >= limit * 0.75 ? 'mid' : 'low';

    const lines = [
        `Context: about ${fmtTokens(ctx.used_tokens)} of ${fmtTokens(ctx.usable_tokens)} tokens (${Math.round(pct)}%)`,
        `Model window ${fmtTokens(ctx.window_tokens)}, less room kept for the reply.`,
    ];
    if (ctx.summary_tokens) lines.push(`Includes a summary of ${ctx.last_compaction?.covered_messages ?? 'earlier'} messages (~${fmtTokens(ctx.summary_tokens)} tokens).`);
    if (ctx.dropped_messages) lines.push(`${ctx.dropped_messages} older message${ctx.dropped_messages === 1 ? '' : 's'} beyond the Memory window, not yet summarized.`);
    if (compacting) lines.push('Compacting the conversation...');
    else if (state.autoCompact === false) lines.push('Auto-compact is off (Settings).');
    else if (ctx.auto_paused) lines.push('Auto-compact paused after failing 3 times. Compact by hand to resume it.');
    else lines.push(`Auto-compacts at ${limit}%.`);
    if (!ctx.measured) lines.push('(Estimated until the next reply measures it.)');
    lines.push('Click to compact.');
    wheel.title = lines.join('\n');
}

/** The wheel was clicked: say what compacting would do, and do it on a yes. */
async function askToCompact() {
    if (busy || last?.compacting) {
        showToast('Already compacting this conversation.', 'info');
        return;
    }
    if (!state.sessionId) {
        showToast('Nothing to compact yet: send a message first.', 'info');
        return;
    }
    await refresh();
    const ctx = last;
    const keep = 4;
    const older = Math.max(0, (ctx?.compactable_messages ?? 0) - keep);
    if (ctx && older === 0) {
        showToast('Nothing to compact yet: the conversation is all recent turns.', 'info');
        return;
    }
    const parts = [];
    if (ctx) parts.push(`The next turn uses about ${fmtTokens(ctx.used_tokens)} of ${fmtTokens(ctx.usable_tokens)} tokens (${Math.round(ctx.percent || 0)}%).`);
    parts.push(`The ${older ? older + ' ' : ''}older messages will be summarized by the model; the newest ${keep} stay word for word. Nothing is deleted, and the summary can be read where the divider appears.`);
    const { confirm } = await import('./confirm-dialog.js');
    const ok = await confirm({
        title: 'Compact this conversation?',
        message: parts.join(' '),
        confirmText: 'Compact',
        danger: false,
    });
    if (ok) await compactNow();
}

async function refresh() {
    const sid = state.sessionId;
    if (!sid) {
        render(null);
        return;
    }
    try {
        const ctx = await fetchSessionContext(sid, { memoryLevel: state.memoryLevel });
        if (state.sessionId !== sid) return;
        render(ctx);
        if (ctx.compacting) schedulePoll();
    } catch {
        // A conversation the server has not saved yet, or a server without
        // compaction: leave the wheel as it is.
    }
}

function schedulePoll() {
    if (pollTimer) return;
    const sid = state.sessionId;
    const startedWith = last?.last_compaction?.message_id ?? null;
    pollTimer = setInterval(async () => {
        if (state.sessionId !== sid) {
            clearInterval(pollTimer);
            pollTimer = null;
            return;
        }
        try {
            const ctx = await fetchSessionContext(sid, { memoryLevel: state.memoryLevel });
            render(ctx);
            if (!ctx.compacting) {
                clearInterval(pollTimer);
                pollTimer = null;
                const lc = ctx.last_compaction;
                if (lc && lc.message_id !== startedWith) {
                    appendDivider(lc);
                    showToast(`Conversation compacted: ${lc.new_messages ?? ''} messages summarized`.replace('  ', ' '), 'success');
                } else if (ctx.last_error) {
                    showToast(`Auto-compact failed: ${ctx.last_error}`, 'error');
                }
            }
        } catch {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }, 3000);
}

/** Build the divider for one compaction marker. */
export function buildCompactionDivider(info) {
    const meta = info.metadata || info;
    const wrap = document.createElement('div');
    wrap.className = 'compaction-divider';
    if (info.message_id || info.id) wrap.dataset.messageId = info.message_id || info.id;

    const head = document.createElement('button');
    head.type = 'button';
    head.className = 'compaction-divider-head';
    const n = meta.new_messages ?? meta.covered_messages;
    const how = meta.trigger === 'manual' ? 'compacted' : 'auto-compacted';
    const words = [`Conversation ${how}`];
    if (n) words.push(`${n} message${n === 1 ? '' : 's'} summarized`);
    if (meta.tokens_before && meta.tokens_after) words.push(`~${fmtTokens(meta.tokens_before)} to ~${fmtTokens(meta.tokens_after)} tokens`);
    head.innerHTML = `
        <span class="compaction-divider-line"></span>
        <span class="compaction-divider-label"></span>
        <svg class="compaction-divider-chevron" viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
        <span class="compaction-divider-line"></span>`;
    head.querySelector('.compaction-divider-label').textContent = words.join(' · ');
    head.setAttribute('aria-expanded', 'false');

    const body = document.createElement('div');
    body.className = 'compaction-divider-body';
    body.hidden = true;
    const summary = info.summary ?? info.content ?? '';
    if (window.marked && window.DOMPurify) {
        body.innerHTML = window.DOMPurify.sanitize(window.marked.parse(summary));
    } else {
        body.textContent = summary;
    }
    head.addEventListener('click', () => {
        const open = body.hidden;
        body.hidden = !open;
        wrap.classList.toggle('open', open);
        head.setAttribute('aria-expanded', String(open));
    });
    wrap.appendChild(head);
    wrap.appendChild(body);
    return wrap;
}

/** A compaction marker message from a loaded history, as its divider. */
export function renderCompactionDivider(msg) {
    if (!elements.chatContainer) return;
    elements.chatContainer.appendChild(
        buildCompactionDivider({ id: msg.id, content: msg.content, metadata: msg.metadata || {} })
    );
}

function appendDivider(info, { beforeLastUser = false } = {}) {
    const box = elements.chatContainer;
    if (!box || !info) return;
    const id = info.message_id;
    if (id && box.querySelector(`.compaction-divider[data-message-id="${id}"]`)) return;
    const divider = buildCompactionDivider(info);
    const users = beforeLastUser ? box.querySelectorAll('.message.user') : [];
    const anchor = users.length ? users[users.length - 1] : null;
    if (anchor) {
        box.insertBefore(divider, anchor);
    } else {
        box.appendChild(divider);
        forceScrollToBottom();
    }
}

/** Compact the current conversation now. `focus`: what the summary should keep. */
export async function compactNow(focus = '') {
    const sid = state.sessionId;
    if (!sid) {
        showToast('Nothing to compact yet: send a message first.', 'info');
        return null;
    }
    if (busy) return null;
    busy = true;
    render(last);
    try {
        const result = await compactSession(sid, { focus });
        if (state.sessionId === sid) {
            appendDivider({ message_id: result.message_id, summary: result.summary, metadata: result.metadata });
            render(result.context || null);
        }
        showToast(`Compacted: ${result.new_messages} messages summarized, ${Math.round(result.percent_before)}% to ${Math.round(result.percent_after)}%`, 'success');
        return result;
    } catch (e) {
        showToast(e.message || 'The compaction failed.', 'error');
        return null;
    } finally {
        busy = false;
        render(last);
        if (state.sessionId === sid) refresh();
    }
}

/** Re-read the wheel, e.g. after a setting that changes it. */
export function refreshContextMeter() {
    if (last) render(last);
    refresh();
}

export function currentContext() {
    return last;
}

export function initContextMeter() {
    wheel = document.getElementById('context-wheel');
    if (!wheel) return;
    wheel.querySelector('.context-wheel-fill').style.strokeDasharray = RING_LENGTH;
    render(null);

    wheel.addEventListener('click', () => askToCompact());

    // Each reply carries the context after it (api.js re-dispatches it).
    document.addEventListener('ragsmith:context', (e) => {
        const ctx = e.detail;
        if (!ctx || (ctx.session_id && ctx.session_id !== state.sessionId)) return;
        render(ctx);
        if (ctx.compacting) schedulePoll();
    });
    // A turn that had to wait for a compaction says so in its stream.
    document.addEventListener('ragsmith:compaction', (e) => {
        const ev = e.detail || {};
        if (ev.state === 'waiting') {
            render({ ...(last || {}), compacting: true });
        } else if (ev.state === 'done') {
            // The summary was stored before this turn's message, so it goes
            // above the bubble just sent, where a reload will show it too.
            appendDivider({ message_id: ev.message_id, summary: ev.summary, metadata: ev.metadata }, { beforeLastUser: true });
            render({ ...(last || {}), compacting: false });
        } else if (ev.state === 'failed') {
            showToast(`Auto-compact failed: ${ev.error}`, 'error');
        }
    });

    // Sessions change in many places (new chat, a loaded conversation, a
    // container switch); watching the id covers all of them.
    setInterval(() => {
        if (state.sessionId !== seenSession) {
            seenSession = state.sessionId;
            last = null;
            render(null);
            if (seenSession) refresh();
        }
    }, 1000);
}
